import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from './supabase'
import { track, Events } from './analytics'

const AuthContext = createContext({})

// Fire the Signup event the first time we see a fresh profile (created
// within the last 5 minutes), then never again for that user. We persist
// the marker so a hard refresh during onboarding doesn't double-fire.
function maybeTrackSignup(profile) {
  if (!profile?.id || !profile?.created_at) return
  const created = new Date(profile.created_at).getTime()
  if (!Number.isFinite(created)) return
  const isFresh = Date.now() - created < 5 * 60 * 1000
  if (!isFresh) return
  const key = `fp_signup_tracked_${profile.id}`
  try {
    if (localStorage.getItem(key)) return
    localStorage.setItem(key, '1')
  } catch { /* ignore */ }
  track(Events.SignupCompleted)
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) fetchProfile(session.user.id)
      else setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)
      if (session?.user) fetchProfile(session.user.id)
      else {
        setProfile(null)
        setLoading(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  async function fetchProfile(userId) {
    const { data } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', userId)
      .single()
    setProfile(data)
    maybeTrackSignup(data)
    setLoading(false)
  }

  async function signUp(email, password, displayName) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { display_name: displayName } }
    })
    if (error) throw error
    return data
  }

  async function signIn(email, password) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
    return data
  }

  async function signInWithProvider(provider) {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: window.location.origin }
    })
    if (error) throw error
    return data
  }

  // Legacy alias kept for existing callers
  const signInWithGoogle = () => signInWithProvider('google')
  const signInWithFacebook = () => signInWithProvider('facebook')
  const signInWithApple = () => signInWithProvider('apple')

  async function signOut() {
    await supabase.auth.signOut()
    setUser(null)
    setProfile(null)
  }

  return (
    <AuthContext.Provider value={{
      user, profile, loading,
      signUp, signIn,
      signInWithProvider, signInWithGoogle, signInWithFacebook, signInWithApple,
      signOut, fetchProfile,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
